/**
 * Application entry.
 *
 * The router is built inside a component so it can be handed the live session
 * as context. Building it at module scope would freeze whatever the session
 * was at import time — which is "signed out", always.
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode, useMemo } from "react";
import { createRoot } from "react-dom/client";

import { ApiError } from "./api/client";
import { captureAttribution } from "./auth/attribution";
import { SessionProvider, useSession } from "./auth/session";
import { buildRouter } from "./router";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A minute. The dashboard reads slowly-changing things — sites, content
      // counts, transcripts — and refetching all of them on every tab focus
      // wakes a free-tier instance for no reason.
      staleTime: 60_000,
      retry(failureCount, error) {
        // Never retry an authentication failure: the client has already tried
        // to refresh once, so a second attempt only delays the redirect to the
        // login screen.
        if (error instanceof ApiError && (error.isAuth || error.status < 500)) return false;
        return failureCount < 2;
      },
    },
  },
});

function App() {
  const session = useSession();
  const router = useMemo(() => buildRouter(session), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Built once, updated in place. Recreating the router on every session
  // change would remount the whole tree and lose scroll position, form state
  // and any open dialog.
  return <RouterProvider router={router} context={{ session }} />;
}

function Boot() {
  const session = useSession();

  // Nothing renders until the stored refresh token has been redeemed or
  // rejected. Rendering earlier means the route guards run against a session
  // that is merely not-loaded-yet, and bounce a signed-in user to /login on
  // every hard refresh.
  if (!session.ready) {
    return (
      <div role="status" className="p-10 text-center text-sm text-muted">
        Loading…
      </div>
    );
  }
  return <App />;
}

// Before the router exists. A visitor who lands on `/?utm_source=…` is
// redirected to /login, where the tags survive only folded into `next` and
// never reach the register form; read them now or the channel is lost.
captureAttribution();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <Boot />
      </SessionProvider>
    </QueryClientProvider>
  </StrictMode>
);
